/**
 * Cleans simulator ornament lines for broadcast play-by-play display.
 * Strips long runs of -, +, ^ and normalizes special “banner” lines to structured labels + body.
 */

export type BroadcastPbpLabelKind = 'gameOver' | 'pinch' | 'relief' | 'defensive';

const SPECIAL_PATTERNS: [BroadcastPbpLabelKind, RegExp][] = [
  // ^…^ patterns: GAME OVER must match before DEFENSIVE CHANGE
  ['gameOver', /\^{2,}\s*GAME OVER\s*\^{2,}/i],
  ['defensive', /\^{2,}\s*DEFENSIVE CHANGE\s*\^{2,}/i],
  // +…+ patterns
  ['relief', /\+{2,}\s*RELIEF PITCHER\s*\+{2,}/i],
  ['pinch', /\+{2,}\s*PINCH HITTER\s*\+{2,}/i],
];

export const BROADCAST_PBP_LABEL_PREFIX: Record<BroadcastPbpLabelKind, string> = {
  gameOver: 'GAME OVER: ',
  pinch: 'PINCH HITTER | ',
  relief: 'RELIEF PITCHER | ',
  defensive: 'DEFENSIVE CHANGE | ',
};

/** Remove decorative runs and empty decoration-only lines; preserve single newlines between kept lines. */
export function stripBroadcastPbpDecoration(s: string): string {
  const lines = s.split('\n');
  const cleaned = lines
    .map((line) =>
      line
        .replace(/-{3,}|\+{3,}|\^{3,}/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    )
    .filter((line) => line.length > 0);
  return cleaned.join('\n').trim();
}

export function parseBroadcastPbpDisplay(raw: string): {
  label: BroadcastPbpLabelKind | null;
  body: string;
} {
  const trimmed = raw.trim();
  for (const [kind, re] of SPECIAL_PATTERNS) {
    if (!re.test(trimmed)) continue;
    const withoutMarker = trimmed.replace(re, ' ');
    const body = stripBroadcastPbpDecoration(withoutMarker);
    return { label: kind, body };
  }
  return { label: null, body: stripBroadcastPbpDecoration(trimmed) };
}

/** Contextual line replacing simulator “Score is H - A” when runs score on a play. */
export function formatScoringPlayLine(params: {
  half: string;
  awayScore: number;
  homeScore: number;
  previousAwayScore: number;
  previousHomeScore: number;
  awayTeamName: string;
  homeTeamName: string;
}): string | null {
  const {
    half,
    awayScore,
    homeScore,
    previousAwayScore,
    previousHomeScore,
    awayTeamName,
    homeTeamName,
  } = params;

  const runsScored =
    Math.max(0, awayScore - previousAwayScore) + Math.max(0, homeScore - previousHomeScore);
  if (runsScored <= 0) return null;

  const isAwayBatting = half === 'top';
  const battingTeamName = isAwayBatting ? awayTeamName : homeTeamName;
  const battingScore = isAwayBatting ? awayScore : homeScore;
  const opponentScore = isAwayBatting ? homeScore : awayScore;
  const prevBattingScore = isAwayBatting ? previousAwayScore : previousHomeScore;
  const prevOpponentScore = isAwayBatting ? previousHomeScore : previousAwayScore;

  if (awayScore === homeScore) {
    return `${battingTeamName} ties the game at ${homeScore}`;
  }

  if (prevBattingScore <= prevOpponentScore && battingScore > opponentScore) {
    return `${battingTeamName} pulls ahead ${battingScore} - ${opponentScore}`;
  }

  if (prevBattingScore > prevOpponentScore && battingScore > opponentScore) {
    return `${battingTeamName} increases their lead to ${battingScore} to ${opponentScore}`;
  }

  if (battingScore < opponentScore) {
    return `${battingTeamName} scores but is still behind ${opponentScore} to ${battingScore}`;
  }

  return `Score is ${homeScore} - ${awayScore}`;
}
